import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { useInvalidatePlanAllowances } from '@renderer/chat/hooks/use-plan-allowances'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { STAGED_ACTIONS_QUERY_KEY, stagedActionExpandedQueryKey } from '../../hooks/staged-actions/utils'

/**
 * Spends one of Free's monthly unblurs on a locked preview. The gateway drops
 * the lock, so the refetched action opens as an ordinary one and drafts on open.
 * A refusal (none left) still refetches the allowances, so the notice turns to Upgrade.
 */
export function useUnlockAction(actionId: string) {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const invalidateAllowances = useInvalidatePlanAllowances()

  return useMutation({
    mutationKey: ['unlock-action', actionId],
    mutationFn: async () => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/planner-v2/unlock'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stagedActionId: actionId }),
      })
      if (!response.ok) {
        throw new Error(`Failed to unblur the action (${response.status})`)
      }
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: stagedActionExpandedQueryKey(actionId) }),
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] }),
      ]),
    onSettled: () => invalidateAllowances(),
  })
}
